const SOA_SELECTORS = [
  "#params_nome_candidato",
  "#params_nome_social",
  "#params_nome_mae",
  "#params_cpf",
  "#params_data_nascimento",
  "#params_sexo",
  "#params_escolaridade",
  "#params_atividade_profissional",
  "#params_telefone",
  "#params_email",
  "#params_logradouro",
  "#params_numero",
  "#params_complemento",
  "#params_bairro",
  "#params_cep",
  "#params_estado",
  "#params_uf",
  "#params_municipio",
  "#autocomplete_admissao_municipio",
  "#admissao_municipio",
  "#params_endereco",
  "#params_empresa_vinculada",
  "#params_cnpj_empresa_vinculada",
]

export function clearSoaFields($j, $form) {
  SOA_SELECTORS.forEach((sel) => {
    const $el = $form.find(sel)
    if (!$el.length) return
    $el.val("").trigger("change")
  })
}

export function applySoaResponseToForm($j, $form, dados, ctx) {
  const { cpfDigits, formatIsoFromBrDate, formatCpf } = ctx

  // =========================
  // Helpers
  // =========================

  const overwrite = !!ctx.overwrite // default: false

  const $find = (selector) => $form.find(selector)

  const getVal = (selector) => {
    const $el = $find(selector)
    if (!$el.length) return null
    return ($el.val() ?? "").toString()
  }

  // Considera "vazio" mesmo se for só espaços, ou máscara sem números (CPF/CEP/telefone)
  const isBlank = (value, { digitsOnly = false } = {}) => {
    if (value == null) return true
    const s = value.toString()
    if (digitsOnly) return s.replace(/\D/g, "").length === 0
    return s.trim().length === 0
  }

  // Decide se pode preencher: se overwrite=true sempre; se não, só se campo estiver vazio
  const canFill = (selector, { digitsOnly = false } = {}) => {
    if (overwrite) return true
    const current = getVal(selector)
    return isBlank(current, { digitsOnly })
  }

  // Highlight rápido para mostrar o que foi preenchido automaticamente
  const flashFilled = ($el) => {
    // evita jogar classes em inputs de componentes muito específicos
    $el.addClass("soa-autofilled")
    setTimeout(() => $el.removeClass("soa-autofilled"), 1400)
  }

  // Preenche com triggers, mas só se existir e (por padrão) estiver vazio
  const setVal = (selector, value, { digitsOnly = false } = {}) => {
    if (value == null) return false
    const v = value.toString()
    if (v.trim() === "") return false

    const $el = $find(selector)
    if (!$el.length) return false
    if (!canFill(selector, { digitsOnly })) return false

    $el
      .val(v)
      .trigger("input")
      .trigger("change")

    flashFilled($el)
    return true
  }

  // Para selects (ex: sexo M/F) — tenta setar e dar change
  const setSelect = (selector, value) => {
    if (value == null) return false
    const v = value.toString()
    if (v.trim() === "") return false

    const $el = $find(selector)
    if (!$el.length) return false
    if (!canFill(selector)) return false

    // se a opção não existir, não seta pra não ficar “quebrado”
    const hasOption = $el.find(`option[value="${cssEscape(v)}"]`).length > 0
    if (!hasOption) return false

    $el.val(v).trigger("change")
    flashFilled($el)
    return true
  }

  // CSS.escape fallback (pra value com caracteres especiais)
  function cssEscape(str) {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(str)
    return String(str).replace(/["\\]/g, "\\$&")
  }

  // Monta endereço “bonito”
  const buildEnderecoCompleto = (logradouro, numero, complemento) => {
    const base = [logradouro, numero].filter(Boolean).join(", ")
    return complemento ? `${base} - ${complemento}` : base
  }

  // =========================
  // DADOS BÁSICOS
  // =========================

  setVal("#params_nome_candidato", dados.nome)

  setVal("#params_nome_social", dados.nome_social)

  setVal("#params_nome_mae", dados.familia?.mae?.nome)

  // CPF no form (se existir) — não sobrescreve se já tiver preenchido
  const cpfFmt = formatCpf(dados.documento || cpfDigits)
  setVal("#params_cpf", cpfFmt, { digitsOnly: true })

  // Data nascimento — V2 retorna ISO 8601 (yyyy-MM-dd), converter para dd/mm/yyyy para o input
  if (dados.data_nascimento) {
    let dataStr = dados.data_nascimento.toString()
    if (/^\d{4}-\d{2}-\d{2}/.test(dataStr)) {
      const [ano, mes, dia] = dataStr.split("T")[0].split("-")
      dataStr = `${dia}/${mes}/${ano}`
    }
    setVal("#params_data_nascimento", dataStr)
  }

  // Sexo: SOA vem "Masculino"/"Feminino"
  if (dados.sexo) {
    const sexo = dados.sexo.toString().toLowerCase()
    if (sexo.includes("masc")) setSelect("#params_sexo", "M")
    if (sexo.includes("fem")) setSelect("#params_sexo", "F")
  }

  setVal("#params_escolaridade", dados.escolaridade)

  setVal("#params_atividade_profissional", dados.atividade_profissional)

  // =========================
  // TELEFONE (prioriza celular)
  // =========================

  if (dados.telefone) {
    // PJ pode ter telefone como string direto
    setVal("#params_telefone", dados.telefone, { digitsOnly: true })
  } else if (dados.telefones) {
    // V2: telefones é array direto, tipo é enum (3 = Celular)
    let telefones = Array.isArray(dados.telefones) ? dados.telefones : []

    const pickCelular =
      telefones.find(t => t.tipo === 3) ||
      telefones[0]

    if (pickCelular && pickCelular.numero) {
      const numero = pickCelular.ddd
        ? `${pickCelular.ddd}${pickCelular.numero}`
        : pickCelular.numero
      setVal("#params_telefone", numero, { digitsOnly: true })
    }
  }

  // =========================
  // EMAIL
  // =========================

  if (dados.email && typeof dados.email === "string") {
    // PJ pode ter email como string direto
    setVal("#params_email", dados.email)
  } else if (dados.emails) {
    // V2: emails é array direto de strings
    const emailsList = Array.isArray(dados.emails) ? dados.emails : []
    if (emailsList.length > 0) {
      setVal("#params_email", emailsList[0]?.email ?? emailsList[0])
    }
  }

  // =========================
  // ENDEREÇO (pega 1º residencial)
  // =========================

  if (dados.enderecos) {
    // V2: enderecos é array direto, tipo é enum (2 = Residencial)
    let enderecos = Array.isArray(dados.enderecos) ? dados.enderecos : []

    const residencial =
      enderecos.find(e => e.tipo === 2) ||
      enderecos[0]

    if (residencial) {
      const logradouro = (residencial.logradouro || "").toString().trim()
      const numero = (residencial.numero || "").toString().trim()
      const complemento = (residencial.complemento || "").toString().trim()
      const bairro = (residencial.bairro || "").toString().trim()
      const cidade = (residencial.cidade || "").toString().trim()
      const estado = (residencial.estado || "").toString().trim()
      const cep = (residencial.cep || "").toString().trim()

      // Se o form tiver campos separados
      setVal("#params_logradouro", logradouro)
      setVal("#params_numero", numero)
      setVal("#params_complemento", complemento)
      setVal("#params_bairro", bairro)

      // Formata o CEP inline (a máscara Stimulus não recebe trigger() sintético do jQuery)
      const cepDigits = cep.replace(/\D/g, '').slice(0, 8)
      const cepFmt = cepDigits.length === 8 ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : cep
      setVal("#params_cep", cepFmt, { digitsOnly: true })

      setVal("#params_estado", estado)
      setVal("#params_uf", estado) // fallback comum em alguns forms

      // Município: o campo auto_complete da admissão gera id="autocomplete_admissao_municipio"
      // (setVal("#params_municipio") usa o seletor errado e não chega ao widget)
      if (cidade) {
        const $muni =
          $find("#autocomplete_admissao_municipio").length ? $find("#autocomplete_admissao_municipio") :
          $find("#admissao_municipio").length             ? $find("#admissao_municipio") :
          $find("#params_municipio")
        if ($muni.length && (overwrite || isBlank($muni.val()))) {
          try { $muni.autocomplete("disable") } catch (_) {}
          $muni.val(cidade).trigger("input").trigger("change")
          try { $muni.autocomplete("enable") } catch (_) {}
          flashFilled($muni)
        }
      }

      // Campo único "endereco" (fallback)
      const enderecoCompleto = buildEnderecoCompleto(logradouro, numero, complemento)
      setVal("#params_endereco", enderecoCompleto)
    }
  }

  // =========================
  // PARTICIPAÇÃO SOCIETÁRIA
  // =========================

  // V2: campo socios[] substitui participacoes.participacao
  const socios = dados.socios || []
  if (Array.isArray(socios) && socios.length > 0) {
    const part = socios[0]
    if (part) {
      const nomeSocio = part.nome || part.razao_social
      if (nomeSocio) setVal("#params_empresa_vinculada", nomeSocio)
      if (part.documento) setVal("#params_cnpj_empresa_vinculada", part.documento, { digitsOnly: true })
    }
  }

  // =========================
  // Pequena melhoria: se quiser, loga no console só em dev
  // =========================
  // console.debug("[SOA][mapper] aplicado", { overwrite, keys: Object.keys(dados || {}) })
}
